import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { Palette, SceneKeys, STAGE_BG_TEXTURE } from '@core/config/constants';
import { MISSIONS, type MissionDefinition } from '@core/config/missions';

const MAX_CARD_WIDTH = 460;

/**
 * Pantalla de misiones: lista objetivos con su barra de progreso y permite reclamar la
 * recompensa en estrellas cuando se completan. El progreso se lee del perfil del jugador.
 */
export class MissionsScene extends BaseScene {
  public constructor() {
    super(SceneKeys.Missions);
  }

  public create(): void {
    this.initResponsive();
    this.addBackground(STAGE_BG_TEXTURE);
    this.addTwinkles(18);

    this.addBackButton();
    this.renderCoins();

    const claimable = MISSIONS.filter(
      (mission) => !this.isClaimed(mission) && mission.progress(this.profile) >= mission.target,
    ).length;

    this.add
      .text(this.centerX, this.scale.height * 0.07, '🎯 Misiones', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '46px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.add
      .text(
        this.centerX,
        this.scale.height * 0.07 + 42,
        claimable > 0 ? `¡Tenés ${claimable} recompensa(s) para reclamar!` : 'Cumplí objetivos y ganá estrellas',
        {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '22px',
          color: claimable > 0 ? '#ffd54f' : '#4dd0e1',
        },
      )
      .setOrigin(0.5);

    this.renderGrid(this.scale.height * 0.2);
  }

  private renderCoins(): void {
    this.add
      .text(this.scale.width - 28, 28, `⭐ ${this.profile.coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);
  }

  private renderGrid(gridTop: number): void {
    const { width, height } = this.scale;
    const gridBottom = height - 40;
    const gridAreaHeight = Math.max(160, gridBottom - gridTop);

    const gap = Math.max(14, width * 0.018);
    const columns = width >= 700 ? 2 : 1;
    const rows = Math.ceil(MISSIONS.length / columns);

    const cardWidth = Math.min(MAX_CARD_WIDTH, (width * 0.94 - (columns - 1) * gap) / columns);
    const cardHeight = Math.min(150, (gridAreaHeight - (rows - 1) * gap) / rows);

    const totalHeight = rows * cardHeight + (rows - 1) * gap;
    const startY = gridTop + Math.max(0, (gridAreaHeight - totalHeight) / 2) + cardHeight / 2;

    MISSIONS.forEach((mission, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const cardsInRow = Math.min(columns, MISSIONS.length - row * columns);
      const rowWidth = cardsInRow * cardWidth + (cardsInRow - 1) * gap;
      const startX = this.centerX - rowWidth / 2 + cardWidth / 2;

      const x = startX + column * (cardWidth + gap);
      const y = startY + row * (cardHeight + gap);
      this.createMissionCard(mission, x, y, cardWidth, cardHeight, index * 40);
    });
  }

  private createMissionCard(
    mission: MissionDefinition,
    x: number,
    y: number,
    cardWidth: number,
    cardHeight: number,
    entranceDelay: number,
  ): void {
    const current = mission.progress(this.profile);
    const claimed = this.isClaimed(mission);
    const complete = current >= mission.target;
    const ratio = Phaser.Math.Clamp(current / mission.target, 0, 1);

    const container = this.add.container(x, y);

    const baseColor = claimed ? 0x2ecc71 : complete ? Palette.primary : Palette.backgroundAlt;
    const background = this.add.graphics();
    background.fillStyle(baseColor, claimed || complete ? 1 : 0.8);
    background.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 18);
    background.lineStyle(4, Palette.highlight, complete && !claimed ? 1 : 0);
    background.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 18);
    container.add(background);

    const padX = 20;
    const leftX = -cardWidth / 2 + padX;

    const emoji = this.add
      .text(leftX, -cardHeight * 0.26, mission.emoji, { fontSize: '34px' })
      .setOrigin(0, 0.5);
    container.add(emoji);

    const title = this.add
      .text(leftX + 50, -cardHeight * 0.3, mission.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '23px',
        color: Palette.textLight,
      })
      .setOrigin(0, 0.5);
    container.add(title);

    const description = this.add
      .text(leftX + 50, -cardHeight * 0.06, mission.description, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '16px',
        color: '#e8dcff',
        wordWrap: { width: cardWidth - padX * 2 - 50 },
      })
      .setOrigin(0, 0.5);
    container.add(description);

    this.addProgressBar(container, cardWidth, cardHeight, padX, ratio, current, mission.target);
    this.addStatus(container, mission, cardWidth, cardHeight, claimed, complete);

    if (complete && !claimed) {
      container.setSize(cardWidth, cardHeight);
      container.setInteractive(
        new Phaser.Geom.Rectangle(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight),
        Phaser.Geom.Rectangle.Contains,
      );
      container.on(Phaser.Input.Events.POINTER_UP, () => this.claim(mission));

      this.tweens.add({
        targets: container,
        scale: { from: 1, to: 1.03 },
        duration: 620,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    container.setAlpha(0);
    this.tweens.add({ targets: container, alpha: 1, duration: 260, delay: entranceDelay });
  }

  private addProgressBar(
    container: Phaser.GameObjects.Container,
    cardWidth: number,
    cardHeight: number,
    padX: number,
    ratio: number,
    current: number,
    target: number,
  ): void {
    const barWidth = cardWidth - padX * 2;
    const barY = cardHeight * 0.24;
    const barHeight = 16;
    const leftX = -cardWidth / 2 + padX;

    const track = this.add.rectangle(leftX, barY, barWidth, barHeight, 0x000000, 0.35).setOrigin(0, 0.5);
    container.add(track);

    const fill = this.add
      .rectangle(leftX, barY, Math.max(2, barWidth * ratio), barHeight, Palette.highlight, 1)
      .setOrigin(0, 0.5);
    container.add(fill);

    const label = this.add
      .text(leftX + barWidth, barY - 26, `${Math.min(current, target)}/${target}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '16px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0.5);
    container.add(label);
  }

  private addStatus(
    container: Phaser.GameObjects.Container,
    mission: MissionDefinition,
    cardWidth: number,
    cardHeight: number,
    claimed: boolean,
    complete: boolean,
  ): void {
    const rightX = cardWidth / 2 - 20;
    let text: string;
    let color: string;

    if (claimed) {
      text = 'Reclamado ✓';
      color = '#ffffff';
    } else if (complete) {
      text = `Reclamar +${mission.reward}⭐`;
      color = '#1a1033';
    } else {
      text = `+${mission.reward}⭐`;
      color = '#ffd54f';
    }

    const status = this.add
      .text(rightX, -cardHeight * 0.3, text, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: complete && !claimed ? '20px' : '18px',
        color,
        backgroundColor: complete && !claimed ? '#ffd54f' : undefined,
        padding: complete && !claimed ? { x: 10, y: 6 } : undefined,
      })
      .setOrigin(1, 0.5);
    container.add(status);
  }

  private isClaimed(mission: MissionDefinition): boolean {
    return mission.daily ? this.profile.isDailyClaimed(mission.id) : this.profile.isMissionClaimed(mission.id);
  }

  private claim(mission: MissionDefinition): void {
    const done = mission.daily
      ? this.profile.claimDaily(mission.id, mission.reward)
      : this.profile.claimMission(mission.id, mission.reward);

    if (done) {
      this.audio.playFanfare();
      this.addConfetti(40);
      this.time.delayedCall(350, () => this.scene.restart());
    }
  }
}
